/**
 * AuthProvider - Contexto de autenticación UNIFICADO para IntelliVerseOne
 * 
 * Este es el ÚNICO lugar que:
 * - Suscribe a supabase.auth.onAuthStateChange
 * - Maneja session, user normalizado, loading, isAuthenticated
 * - Provee logout() y requireAuth()
 * 
 * Todos los demás contextos (UserSessionContext, UserContext) consumen de aquí.
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase, hasSupabaseEnv } from '../lib/supabaseClient';
import { fetchProfileBundle, normalizePlan, normalizeUser } from '../features/profile/api';

// Flag de debug solo en desarrollo
const DEBUG_AUTH = process.env.NODE_ENV === 'development';
const PROFILE_TIMEOUT_MS = 30000;

async function withTimeout(promise, ms = PROFILE_TIMEOUT_MS) {
  let timer;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('IV1_PROFILE_TIMEOUT')), ms);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timer);
  }
}

async function waitForSessionReady(expectedUserId, maxMs = 3000) {
  const startedAt = Date.now();
  while (Date.now() - startedAt < maxMs) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id && session.user.id === expectedUserId) return true;
    } catch {}
    await new Promise((resolve) => setTimeout(resolve, 120));
  }
  return false;
}

function sanitizeDebugErrorMessage(message) {
  const raw = String(message || '');
  return raw
    .replace(/Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi, 'Bearer [redacted]')
    .replace(/eyJ[A-Za-z0-9\-_=]+\.[A-Za-z0-9\-_=]+\.?[A-Za-z0-9\-_.+/=]*/g, '[jwt-redacted]')
    .slice(0, 220);
}

/**
 * Guard estricto: ¿estamos en un entorno de dev/test/e2e/localhost?
 * Solo en estos entornos se permite usar iv1_dev_session como harness de testing.
 */
function isDevTestEnv() {
  try {
    if (process.env.NODE_ENV === 'test') return true;
    if (process.env.REACT_APP_E2E === 'true') return true;
    if (process.env.NODE_ENV === 'development') {
      const h = window.location.hostname;
      if (h === 'localhost' || h === '127.0.0.1') return true;
    }
  } catch {}
  return false;
}

/**
 * Lee iv1_dev_session de localStorage y construye un user sintético mínimo.
 * Retorna null si no existe, no es parseable, o estamos fuera de entorno permitido.
 */
function getDevSessionUser() {
  if (!isDevTestEnv()) return null;
  try {
    const raw = localStorage.getItem('iv1_dev_session');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.email) return null;
    const plan = localStorage.getItem('iv1_plan_override') || 'trial';
    return {
      id: parsed.id || `dev-${parsed.email}`,
      email: parsed.email,
      name: parsed.name || parsed.email.split('@')[0],
      emailVerified: true,
      plan,
      trialDaysLeft: plan === 'trial' ? 7 : 0,
      referralCode: 'DEV',
      referrals: { sent: 0, successful: 0, bonusDays: 0 },
      achievements: [],
      preferences: {
        language: 'es',
        theme: 'dark',
        email_notifications: false,
        push_notifications: false,
        marketing_optin: false,
        safe_mode: true,
        timezone: 'America/Bogota'
      }
    };
  } catch {
    return null;
  }
}

// Contexto principal de autenticación
const AuthContext = createContext(null);

/**
 * Hook para acceder al contexto de autenticación
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
}

/**
 * AuthProvider - Proveedor unificado de autenticación
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null); // user normalizado con perfil completo
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Refs para evitar race conditions
  const mountedRef = useRef(true);
  const loadingProfileRef = useRef(false);
  const logoutInProgressRef = useRef(false);

  const readLastKnownPlan = useCallback((userId) => {
    if (!userId) return null;
    try {
      const cached = localStorage.getItem(`iv1_last_plan:${userId}`);
      return cached ? normalizePlan(cached) : null;
    } catch {
      return null;
    }
  }, []);

  const writeLastKnownPlan = useCallback((userId, plan) => {
    if (!userId || !plan) return;
    try {
      localStorage.setItem(`iv1_last_plan:${userId}`, normalizePlan(plan));
    } catch {}
  }, []);

  /**
   * Carga el perfil completo del usuario desde Supabase
   */
  const loadUserProfile = useCallback(async (currentSession) => {
    if (!currentSession?.user?.id) {
      if (DEBUG_AUTH) console.log('[AuthProvider] loadUserProfile: No session/user, setting null');
      setUser(null);
      return null;
    }

    // Evitar cargas duplicadas
    if (loadingProfileRef.current) {
      if (DEBUG_AUTH) console.log('[AuthProvider] loadUserProfile: Already loading, skipping');
      return user;
    }
    loadingProfileRef.current = true;
    setProfileLoading(true);
    if (DEBUG_AUTH) console.log('[AuthProvider] loadUserProfile: Fetching profile for user', currentSession.user.id);

    try {
      // FIX: eliminada llamada a waitForSessionReady() que causaba deadlock
      // al invocar getSession() dentro del lock de onAuthStateChange(INITIAL_SESSION).
      // currentSession ya contiene la sesión válida entregada por el caller.
      const bundle = await withTimeout(fetchProfileBundle(currentSession));
      const normalized = normalizeUser(bundle);
      
      // Agregar campos de la sesión que no vienen en el bundle
      normalized.emailVerified = !!currentSession.user.email_confirmed_at;
      normalized.email = normalized.email || currentSession.user.email || '';
      
      // Cache trial_ends_at para fallback en navegaciones futuras
      if (normalized.plan === 'trial' && bundle.planRow?.trial_ends_at) {
        try {
          localStorage.setItem(`iv1_trial_ends:${currentSession.user.id}`, bundle.planRow.trial_ends_at);
        } catch {}
      }

      if (mountedRef.current && !logoutInProgressRef.current) {
        normalized.planDebug = {
          ...(normalized.planDebug || {}),
          bundleErrorCode: null,
          bundleErrorMessage: null,
        };
        setUser(normalized);
        setLoading(false);
        writeLastKnownPlan(currentSession.user.id, normalized.plan);
        if (DEBUG_AUTH) console.log('[AuthProvider] loadUserProfile: Profile loaded successfully', { id: normalized.id, plan: normalized.plan });
      }
      return normalized;
    } catch (error) {
      console.warn('[AuthProvider] Error cargando perfil:', error.message);
      if (DEBUG_AUTH) console.error('[AuthProvider] loadUserProfile: Error details', error);

      let fallbackPlanFromDb = null;
      let fallbackTrialDaysLeft = null;
      try {
        const { data: planData } = await supabase
          .from('plans')
          .select('plan, trial_ends_at')
          .eq('user_id', currentSession.user.id)
          .limit(1)
          .maybeSingle();

        fallbackPlanFromDb = planData?.plan ? normalizePlan(planData.plan) : null;
        if (planData?.trial_ends_at) {
          const end = new Date(planData.trial_ends_at).getTime();
          const now = Date.now();
          fallbackTrialDaysLeft = Number.isFinite(end)
            ? Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)))
            : null;
        }
      } catch {
        // no-op: mantener fallback de metadata/cache
      }

      // Si la re-consulta a plans también falló, leer trial_ends_at cacheado
      if (fallbackTrialDaysLeft === null) {
        try {
          const cachedEnds = localStorage.getItem(`iv1_trial_ends:${currentSession.user.id}`);
          if (cachedEnds) {
            const diffMs = new Date(cachedEnds).getTime() - Date.now();
            const computed = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
            if (Number.isFinite(computed)) fallbackTrialDaysLeft = computed;
          }
        } catch {}
      }
      
      // Fallback de plan: priorizar DB > cache > metadata > 'trial'
      const fallbackPlan = fallbackPlanFromDb
        || readLastKnownPlan(currentSession.user.id)
        || (currentSession.user?.user_metadata?.plan ? normalizePlan(currentSession.user.user_metadata.plan) : null)
        || (currentSession.user?.app_metadata?.plan ? normalizePlan(currentSession.user.app_metadata.plan) : null)
        || 'trial';

      const fallbackUser = {
        id: currentSession.user.id,
        email: currentSession.user.email || '',
        name: currentSession.user.user_metadata?.name || '',
        emailVerified: !!currentSession.user.email_confirmed_at,
        plan: fallbackPlan,
        trialDaysLeft: fallbackPlan === 'trial' ? (fallbackTrialDaysLeft ?? 7) : 0,
        referralCode: '—',
        referrals: { sent: 0, successful: 0, bonusDays: 0 },
        achievements: [],
        preferences: {
          language: 'es',
          theme: 'dark',
          email_notifications: true,
          push_notifications: false,
          marketing_optin: true,
          safe_mode: true,
          timezone: 'America/Bogota'
        },
        planDebug: {
          rawPlanFromPlansTable: fallbackPlanFromDb,
          rawPlanFromProfile: null,
          normalizedPlan: fallbackPlan,
          sourceUsed: fallbackPlanFromDb ? 'plans' : 'fallback',
          lastBundleFetchAt: new Date().toISOString(),
          bundleErrorCode: error?.code || error?.name || 'BUNDLE_FETCH_ERROR',
          bundleErrorMessage: sanitizeDebugErrorMessage(error?.message || String(error)),
        },
      };
      
      if (mountedRef.current && !logoutInProgressRef.current) {
        setUser(fallbackUser);
        setLoading(false);
      }
      return fallbackUser;
    } finally {
      loadingProfileRef.current = false;
      if (mountedRef.current) {
        setProfileLoading(false);
      }
    }
  }, []);

  /**
   * Inicialización: obtener sesión existente al montar
   */
  useEffect(() => {
    mountedRef.current = true;

    const initSession = async () => {
      // ── Dev/Test harness: iv1_dev_session bypass ──
      // Si estamos en dev/test/localhost y hay iv1_dev_session, usar user sintético
      // sin tocar Supabase. Permite que E2E funcione sin sesión real.
      const devUser = getDevSessionUser();
      if (devUser) {
        if (DEBUG_AUTH) console.log('[AuthProvider] 🧪 Dev session detected, using synthetic user:', devUser.id, '| plan:', devUser.plan);
        setUser(devUser);
        setSession({ user: { id: devUser.id, email: devUser.email } }); // session mínima
        setLoading(false);
        return;
      }

      if (!hasSupabaseEnv) {
        console.warn('[AuthProvider] Supabase no configurado. Usuario no autenticado.');
        setLoading(false);
        return;
      }

      // FIX: NO llamar a getSession() aquí.
      // supabase-js v2.50+ usa navigator.locks en _initialize(), y getSession()
      // compite por el mismo lock, causando contención. La hidratación de sesión
      // se delega a onAuthStateChange(INITIAL_SESSION) que Supabase emite siempre
      // al iniciar, con la sesión ya resuelta y el lock liberado.
      // loading se mantiene en true hasta que INITIAL_SESSION lo resuelva.
    };

    initSession();

    return () => {
      mountedRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ÚNICO listener de onAuthStateChange
   */
  useEffect(() => {
    if (!hasSupabaseEnv) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      // Ignorar eventos si logout está en progreso
      if (logoutInProgressRef.current) {
        if (DEBUG_AUTH) console.log('[AuthProvider] Auth event ignored (logout in progress):', event);
        return;
      }

      if (DEBUG_AUTH) console.log('[AuthProvider] Auth event:', event, { hasSession: !!newSession });

      // FIX: NO hacer await de loadUserProfile dentro de este callback.
      // supabase-js v2.50+ ejecuta onAuthStateChange DENTRO del navigator.locks
      // lock de _initialize(). Si hacemos await de loadUserProfile, sus queries
      // REST llaman getSession() internamente, que intenta adquirir el mismo lock
      // → deadlock. Solución: fire-and-forget controlado.
      switch (event) {
        case 'SIGNED_IN':
        case 'TOKEN_REFRESHED':
        case 'USER_UPDATED':
          if (newSession && mountedRef.current) {
            setSession(newSession);
            // Cargar perfil solo en SIGNED_IN o USER_UPDATED, no en cada refresh de token
            if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
              setLoading(true);
              loadUserProfile(newSession)
                .catch(err => console.warn('[AuthProvider] profile load failed on', event, ':', err?.message));
            }
          }
          break;

        case 'SIGNED_OUT':
          if (mountedRef.current) {
            setSession(null);
            setUser(null);
          }
          break;

        default:
          // INITIAL_SESSION, PASSWORD_RECOVERY, etc.
          if (newSession && mountedRef.current) {
            setSession(newSession);
            // Hidratar perfil en INITIAL_SESSION (evita user=null con sesión válida)
            loadUserProfile(newSession)
              .catch(err => console.warn('[AuthProvider] ⚠️ profile hydration failed on', event, ':', err?.message));
          } else if (mountedRef.current) {
            // Sin sesión en INITIAL_SESSION => no autenticado, bajar loading
            setLoading(false);
          }
          break;
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Logout robusto: limpia todo y redirige
   */
  const logout = useCallback(async () => {
    if (logoutInProgressRef.current) {
      if (DEBUG_AUTH) console.log('[AuthProvider] logout: Already in progress, skipping');
      return;
    }
    
    if (DEBUG_AUTH) console.log('[AuthProvider] logout: Starting ATOMIC logout sequence');
    logoutInProgressRef.current = true;

    // 1. Limpiar estado de React PRIMERO (no más UI autenticada)
    setSession(null);
    setUser(null);
    setLoading(false);

    // 2. Limpiar storage ANTES de signOut (si signOut falla, tokens no sobreviven)
    if (DEBUG_AUTH) console.log('[AuthProvider] logout: Cleaning storage FIRST');
    try {
      Object.keys(window.localStorage)
        .filter(k => k.startsWith('sb-') || k.startsWith('supabase') || k === 'iv1_dev_session')
        .forEach(k => window.localStorage.removeItem(k));
      Object.keys(window.sessionStorage)
        .filter(k => k.startsWith('sb-') || k.startsWith('supabase'))
        .forEach(k => window.sessionStorage.removeItem(k));
      document.cookie.split(';').forEach(c => {
        const name = c.split('=')[0].trim();
        if (name.startsWith('sb-')) {
          document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
          document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}; SameSite=Lax`;
        }
      });
    } catch (e) {
      console.warn('[AuthProvider] Error limpiando storage:', e);
    }

    // 3. Fire-and-forget signOut (NO await — así el redirect es INMEDIATO)
    if (hasSupabaseEnv) {
      if (DEBUG_AUTH) console.log('[AuthProvider] logout: fire-and-forget signOut');
      supabase.auth.signOut({ scope: 'global' }).catch(e => {
        console.warn('[AuthProvider] signOut failed (tokens already cleared):', e);
      });
    }

    // 4. Redirigir a login INMEDIATAMENTE (no espera signOut)
    if (DEBUG_AUTH) console.log('[AuthProvider] logout: Redirecting to /login');
    logoutInProgressRef.current = false;
    window.location.href = '/login';
  }, []);

  /**
   * Requerir autenticación: abre el modal si no hay sesión
   */
  const requireAuth = useCallback((options = {}) => {
    if (user?.id) {
      return true;
    }
    
    // Emitir evento para abrir el modal de autenticación
    try {
      window.dispatchEvent(new CustomEvent('auth:open', {
        detail: {
          view: options.view || 'login',
          redirectTo: options.redirectTo || window.location.pathname,
          origin: options.origin || 'requireAuth'
        }
      }));
    } catch (e) {
      console.warn('[AuthProvider] Error abriendo modal:', e);
    }
    
    return false;
  }, [user?.id]);

  /**
   * Recargar perfil manualmente (útil después de guardar cambios)
   */
  const refreshProfile = useCallback(async () => {
    if (session) {
      return await loadUserProfile(session);
    }
    return null;
  }, [session, loadUserProfile]);

  // Valores derivados
  const isAuthenticated = !!(session && user?.id);

  const value = {
    // Estado
    session,
    user,
    loading,           // SOLO carga de sesión (getSession). Se hace false rápido (<200ms)
    profileLoading,    // Carga de perfil completo (Supabase queries). Puede tardar segundos
    isAuthenticated,
    
    // Acciones
    logout,
    requireAuth,
    refreshProfile,
    
    // Para compatibilidad con useUserSession
    // (session.user es el user de Supabase, user es el normalizado)
    sessionUser: session?.user || null,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
